import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { formatMoney } from '@/utils/money';

interface Props {
  amount: number | undefined;
  onChange: (amount: number | null) => void;
}

/**
 * A contractor has no salary and no expenses, so every figure on their row is
 * one somebody typed. Tapping the cell turns it into an input; clearing it and
 * committing removes the month rather than recording a payment of zero.
 */
const ContractorCell: React.FC<Props> = ({ amount, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (isEditing) setDraft(amount === undefined ? '' : String(amount));
  }, [isEditing]);

  const commit = () => {
    setIsEditing(false);

    const trimmed = draft.trim();
    if (!trimmed) {
      if (amount !== undefined) onChange(null);
      return;
    }

    const parsed = Number(trimmed.replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0 || parsed === amount) return;

    onChange(parsed);
  };

  if (!isEditing) {
    return (
      <Editable onClick={() => setIsEditing(true)}>
        {amount === undefined ? '+' : formatMoney(amount)}
      </Editable>
    );
  }

  return (
    <Input
      autoFocus
      value={draft}
      inputMode="decimal"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setIsEditing(false);
      }}
    />
  );
};

const Editable = styled.span`
  cursor: pointer;
  border-bottom: dashed 1px #dae1e8;
`;

const Input = styled.input`
  width: 84px;
  height: 30px;
  text-align: right;
  border-radius: 4px;
  border: solid 1px #fb7e14;
  padding: 0 6px;
  font-size: 14px;
  outline: none;
`;

export default ContractorCell;
